import type { UIMessage } from './MessageList';

interface Indexed {
  readonly msg: UIMessage;
  /** Position in the flat message list. */
  readonly idx: number;
}

/** A question and Moo's reply to it. A conversation that opens with Moo
 *  speaking first has a turn with no question. */
export interface Turn {
  readonly key: string;
  readonly user: Indexed | null;
  readonly assistants: readonly Indexed[];
}

/** Index of the first reply that is still live: every message after the
 *  user's latest one. Live replies keep their cards clickable and are never
 *  folded. A card button's result lands as a further assistant message with no
 *  question before it, so it joins the live replies rather than retiring the
 *  card that produced it. Sending a new question ends them all at once, before
 *  any reply to it arrives. */
export function firstLiveIndex(messages: readonly UIMessage[]): number {
  return messages.findLastIndex((m) => m.role === 'user') + 1;
}

/** Splits the flat message list into turns: each user message starts one,
 *  and every assistant message after it, up to the next user message,
 *  belongs to it. */
export function groupTurns(messages: readonly UIMessage[]): Turn[] {
  const turns: { key: string; user: Indexed | null; assistants: Indexed[] }[] =
    [];
  messages.forEach((msg, idx) => {
    const current = turns.at(-1);
    if (msg.role === 'user' || !current) {
      turns.push({
        key: msg.id,
        user: msg.role === 'user' ? { msg, idx } : null,
        assistants: msg.role === 'assistant' ? [{ msg, idx }] : [],
      });
      return;
    }
    current.assistants.push({ msg, idx });
  });
  return turns;
}
